/**
 * Pure decision function for the registration wizard's "where should
 * this user resume?" effect. Lives outside the React component so we
 * can exhaustively unit-test the routing matrix at deploy pre-flight.
 *
 * The bug it guards against: <PostAuthGate> in App.tsx redirects to
 * /register whenever me.onboarding.complete is false, but the wizard
 * (until 2026-04-29) would in turn navigate('/dashboard') if the user
 * had a firm + active trial — even when their profile was missing
 * firstName/phone. Result: an infinite reload loop. The invariant the
 * wizard MUST honour is: when onboarding is incomplete, never exit
 * to /dashboard.
 */

export type WizardRouteMe = {
  user: { firstName?: string | null };
  memberships: Array<{
    firm: {
      subscriptionStatus?: string | null;
      trialEndsAt?: string | null;
    };
  }>;
  onboarding?: { complete: boolean };
};

export type WizardRouteDecision =
  | { kind: 'goto'; path: '/dashboard' }
  | { kind: 'step'; step: 1 | 2 | 3 };

export function decideWizardResume(
  me: WizardRouteMe,
  now: number = Date.now(),
): WizardRouteDecision {
  const hasFirm = me.memberships.length > 0;
  const firm = me.memberships[0]?.firm;
  const subStatus = firm?.subscriptionStatus;
  const subActive =
    subStatus === 'active' ||
    (subStatus === 'trialing' &&
      !!firm?.trialEndsAt &&
      new Date(firm.trialEndsAt).getTime() > now);
  // Older clients that hit a pre-onboarding-rules api won't see the
  // onboarding key. Fall back to "having a firm means complete" which
  // matches the previous behaviour and avoids breaking those users.
  const onboardingComplete = me.onboarding?.complete ?? hasFirm;

  if (onboardingComplete && hasFirm && subActive) {
    return { kind: 'goto', path: '/dashboard' };
  }
  if (!onboardingComplete) {
    // Step 1 collects firstName/phone (and firmName); the idempotent
    // POST /api/firms in step 2 backfills the user row when a firm
    // already exists.
    return { kind: 'step', step: 1 };
  }
  if (hasFirm && !subActive) {
    return { kind: 'step', step: 3 };
  }
  if (me.user.firstName) {
    return { kind: 'step', step: 2 };
  }
  return { kind: 'step', step: 1 };
}
